import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from './PageLayout';
import theme, { mobileBreakpoint } from '../theme';
import { StarBadgeWithIcon } from './shared/CommonElements';
import texts from '../locales/texts';
import { LESSON_CONFIG } from '../config/lessonConfig';
import config from '../config';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 10;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 10;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  color: ${props => props.theme.colors.primary};
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 0 10px;
  }
`;

const FAQItem = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-left: 10px solid ${props => props.borderColor || props.theme.colors.accent2};
  border-right: 2px solid ${props => props.borderColor || props.theme.colors.accent2};
  border-top: 2px solid ${props => props.borderColor || props.theme.colors.accent2};
  border-bottom: 2px solid ${props => props.borderColor || props.theme.colors.accent2};
  margin: 24px auto;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 24px 32px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  font-weight: 600;
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 20px 18px;
    font-size: 1.1rem;
  }
  
`;

const FAQContainerHover = styled.div`
  &:hover {
    background-color: rgba(188, 231, 253, 0.1);
  }
`;

const FAQAnswer = styled.div`
  padding: 0 32px;
  padding-bottom: ${props => props.isOpen ? '32px' : '0'};
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  line-height: 1.7;
  opacity: ${props => props.isOpen ? 1 : 0};
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 0 18px;
    padding-bottom: ${props => props.isOpen ? '24px' : '0'};
    font-size: 1rem;
  }
  
  p {
    margin: ${props => props.isOpen ? '0 0 24px 0' : '0'};
    transition: margin 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:last-child p {
    margin-bottom: 0;
  }
`;

const Icon = styled.span`
  font-size: 1.5rem;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  margin-left: 16px;
  flex-shrink: 0;
`;

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useEffect(() => {
    const viewportWidth = window.innerWidth;
    fetch(config.getApiUrl(`/api/common/faq?viewportWidth=${viewportWidth}`), { method: 'GET' })
      .catch(() => {});
  }, []);

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <StarBadgeWithIcon />
          <MainTitle>{texts.faqTitle}</MainTitle>
        </HeroTitle>
        <Subtitle>{texts.faqSubtitle}</Subtitle>
        <Description>{texts.faqDescription}</Description>
      </HeroSection>
      
      <FAQContainer>
        {texts.faqItems && texts.faqItems.map((item, index) => (
          <FAQItem 
            key={index}
            borderColor={index % 3 === 0 ? theme.colors.accent2 : index % 3 === 1 ? theme.colors.accent3 : theme.colors.secondary}
          >
            <FAQContainerHover>
            <FAQQuestion onClick={() => toggleItem(index)}>
              <span>{item.question}</span>
              <Icon isOpen={openItems[index]}>▼</Icon>
            </FAQQuestion>
            <FAQAnswer isOpen={openItems[index]}>
              <p dangerouslySetInnerHTML={{ 
                __html: item.answer.replace(
                  /24 Stunden/g, 
                  `${LESSON_CONFIG.CANCELLATION_RESTRICTION_HOURS} Stunden`
                )
              }} />
            </FAQAnswer>

            </FAQContainerHover>
          </FAQItem>
        ))}
      </FAQContainer>
    </PageLayout>
  );
};

export default FAQ;

